import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Services() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [titleVisible, setTitleVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState<boolean[]>([]);
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === sectionRef.current && entry.isIntersecting) {
            setIsVisible(true);
          }
          if (entry.target === titleRef.current && entry.isIntersecting) {
            setTimeout(() => setTitleVisible(true), 300);
          }
          cardRefs.current.forEach((card, index) => {
            if (entry.target === card && entry.isIntersecting) {
              setTimeout(() => {
                setCardsVisible(prev => {
                  const newState = [...prev];
                  newState[index] = true;
                  return newState;
                });
              }, 600 + index * 200);
            }
          });
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (titleRef.current) observer.observe(titleRef.current);
    cardRefs.current.forEach(card => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const mainCourses = [
    {
      title: '研磨なしコース',
      price: '21,000円〜',
      description: '洗車とコーティングのベーシックコース',
      features: ['丁寧な手洗い車', '基本処理', 'ボディコーティング', '耐久年数1〜2年', 'アフターサポート'],
      icon: 'ri-car-washing-line',
      popular: false
    },
    {
      title: '軽研磨コース',
      price: '30,000円〜',
      description: '軽微な傷の除去とコーティング',
      features: ['軽研磨による傷除去', '丁寧な手洗い車', 'プレミアムコーティング', '耐久年数1〜2年', 'アフターサポート'],
      icon: 'ri-contrast-2-line',
      popular: true
    },
    {
      title: '２層コーティングコース',
      price: '70,000円〜',
      description: '最高級の保護と輝きを実現',
      features: ['本格研磨作業', '２層コーティング施工', '耐久年数3〜5年', '定期メンテナンス', 'プレミアムサポート'],
      icon: 'ri-vip-crown-line',
      popular: false
    }
  ];

  const additionalOptions = [
    {
      title: 'ホイールコーティング',
      price: '18,000円〜',
      description: 'ブレーキダストを簡単除去',
      icon: 'ri-steering-2-line'
    },
    {
      title: '窓ガラスコーティング',
      price: '4,000円〜',
      description: '撥水効果で視界クリア',
      icon: 'ri-window-line'
    }
  ];

  return (
    <section 
      id="services" 
      ref={sectionRef}
      className={`py-32 bg-luxury-gray transition-all duration-[1500ms] ease-out ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div 
          ref={titleRef}
          className={`text-center mb-20 transition-all duration-[1500ms] ease-out ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="w-16 h-0.5 bg-luxury-navy mx-auto mb-6"></div>
          <h2 className="text-4xl font-light text-luxury-navy mb-6 tracking-tight">サービス内容</h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            お客様のニーズに合わせた3つのメインコースと、追加オプションをご用意しております。<br />
            料金シミュレーターをご用意しておりますので、予約フォームよりご確認ください。
          </p>
        </div>

        {/* Main Courses */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {mainCourses.map((course, index) => (
            <div
              key={index}
              ref={el => cardRefs.current[index] = el}
              className={`bg-white rounded-md overflow-hidden border hover:shadow-md transition-all duration-500 ${
                course.popular ? 'border-luxury-navy relative' : 'border-gray-200'
              } ${
                cardsVisible[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              {course.popular && (
                <div className="bg-luxury-navy text-white text-center py-2.5 text-sm font-medium tracking-wide">
                  <i className="ri-star-fill mr-2"></i>
                  人気No.1
                </div>
              )}
              <div className="p-10">
                <div className="w-14 h-14 bg-luxury-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${course.icon} text-luxury-navy text-2xl`}></i>
                </div>
                <h3 className="text-xl font-medium text-luxury-navy mb-3 text-center tracking-tight">{course.title}</h3>
                <div className="text-2xl font-light text-luxury-navy mb-4 text-center tracking-tight">{course.price}</div>
                <p className="text-sm text-gray-500 mb-8 text-center font-light">{course.description}</p>
                <ul className="space-y-3">
                  {course.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600 text-sm">
                      <i className="ri-check-line text-luxury-navy mr-3 text-lg flex-shrink-0"></i>
                      <span className="font-light">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Options */}
        <div 
          className={`transition-all duration-[1500ms] ease-out ${
            cardsVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h3 className="text-2xl font-light text-luxury-navy mb-10 text-center tracking-tight">追加オプション</h3>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {additionalOptions.map((option, index) => (
              <div
                key={index}
                ref={el => cardRefs.current[mainCourses.length + index] = el}
                className={`bg-white rounded-md p-10 border border-gray-200 hover:shadow-md transition-all duration-500 ${
                  cardsVisible[mainCourses.length + index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                <div className="w-12 h-12 bg-luxury-navy/5 rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className={`${option.icon} text-luxury-navy text-xl`}></i>
                </div>
                <h4 className="text-lg font-medium text-luxury-navy mb-2 text-center tracking-tight">{option.title}</h4>
                <div className="text-xl font-light text-luxury-navy mb-3 text-center tracking-tight">{option.price}</div>
                <p className="text-sm text-gray-500 text-center font-light">{option.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Car Wash Service */}
        <div 
          className={`mt-20 transition-all duration-[1500ms] ease-out ${
            cardsVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-white rounded-md p-12 border border-gray-200">
            <div className="text-center mb-10">
              <div className="w-14 h-14 bg-luxury-navy/5 rounded-full flex items-center justify-center mx-auto mb-5">
                <i className="ri-truck-line text-luxury-navy text-2xl"></i>
              </div>
              <h3 className="text-2xl font-light text-luxury-navy mb-4 tracking-tight">出張洗車サービス</h3>
              <p className="text-base text-gray-600 font-light">
                ご自宅や職場までお伺いして、お忙しい方にも安心してご利用いただけます。
              </p>
            </div>

            <div className="bg-luxury-gray rounded-md p-10 max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-10">
                {/* Left Column - Service Details */}
                <div className="border-r border-gray-300 pr-10">
                  <h4 className="text-lg font-medium text-luxury-navy mb-5 flex items-center">
                    <i className="ri-service-line text-luxury-navy mr-2"></i>
                    対応コース
                  </h4>
                  <div className="space-y-3">
                    <p className="text-base font-medium text-luxury-navy">
                      出張洗車(簡易コーティング)コース
                    </p>
                    <p className="text-xl font-light text-luxury-navy tracking-tight">
                      10,000円〜
                    </p>
                    <p className="text-sm text-gray-500 flex items-center font-light">
                      <i className="ri-add-circle-line text-luxury-navy mr-2"></i>
                      オプション追加可能
                    </p>
                  </div>
                </div>

                {/* Right Column - Travel Fee */}
                <div className="pl-0 md:pl-10">
                  <h4 className="text-lg font-medium text-luxury-navy mb-5 flex items-center">
                    <i className="ri-map-pin-distance-line text-luxury-navy mr-2"></i>
                    出張費
                  </h4>
                  <div className="space-y-3">
                    <p className="text-xl font-light text-luxury-blue tracking-tight">
                      交通費1kmあたり160円
                    </p>
                    <p className="text-sm text-gray-500 flex items-center font-light">
                      <i className="ri-information-line text-gray-400 mr-2"></i>
                      拠点からの距離で計算
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-gray-300">
                <button
                  onClick={() => navigate('/delivery-reserve')}
                  className="w-full bg-luxury-navy text-white py-3.5 rounded-md text-sm font-medium hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center shadow-sm"
                >
                  <i className="ri-calendar-check-line mr-2"></i>
                  出張洗車を予約する
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
